import { ChangeEvent, useEffect, useState } from "react";
import { isAxiosError } from "axios";
import {
  createProductCategory,
  updateProductCategory,
} from "../../../services";
import { ProductCategory } from "../../../types/product.type";
import { FieldLabel } from "../shared/FormField";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";

// Mirrors the `name varchar(200)` column the product-categories API validates against.
const MAX_NAME_LENGTH = 200;

const DIALOG_WIDTH = 360;

/** The API refuses a name another category already holds with a 409. */
const CONFLICT_STATUS = 409;

const DUPLICATE_NAME_MESSAGE = "มีหมวดสินค้าชื่อนี้อยู่แล้ว กรุณาใช้ชื่ออื่น";
const CREATE_FAILED_MESSAGE = "เพิ่มหมวดสินค้าไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
const UPDATE_FAILED_MESSAGE = "แก้ไขหมวดสินค้าไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";

/** Names the failure in the dialog, where the field that caused it is still on screen. */
const resolveSaveErrorMessage = (
  error: unknown,
  fallbackMessage: string,
): string =>
  isAxiosError(error) && error.response?.status === CONFLICT_STATUS
    ? DUPLICATE_NAME_MESSAGE
    : fallbackMessage;

interface ProductCategoryDialogProps {
  open: boolean;
  /** The category being renamed; leave it out to add a new one. */
  category?: ProductCategory | null;
  onClose: () => void;
  /** Code of the saved category — for a new one, the code the API generated. */
  onSaved: (code: string) => void;
}

/**
 * Adds or renames a หมวดสินค้า.
 *
 * The name is the only thing to fill in: the code is generated by the API on create and
 * never changes, so nothing here can guess, reserve or edit one.
 */
export default function ProductCategoryDialog({
  open,
  category,
  onClose,
  onSaved,
}: ProductCategoryDialogProps) {
  const [name, setName] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);

  const isEdit = Boolean(category);

  useEffect(() => {
    if (!open) return;
    setName(category?.name ?? "");
    setError("");
  }, [open, category]);

  const handleChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    setError("");
  };

  /** Resolves with the saved category's code, or "" when there is nothing to select. */
  const saveCategory = async (trimmedName: string): Promise<string> => {
    if (category) {
      await updateProductCategory(category.uuid, { name: trimmedName });
      return category.code;
    }
    const { data: body } = await createProductCategory({ name: trimmedName });
    // The caller selects the new category by the code the API generated, so a response
    // without the stored row leaves nothing to select.
    return body.data?.code ?? "";
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("กรุณากรอกชื่อหมวดสินค้า");
      return;
    }

    const failedMessage = isEdit
      ? UPDATE_FAILED_MESSAGE
      : CREATE_FAILED_MESSAGE;
    setSaving(true);
    try {
      const savedCode = await saveCategory(trimmedName);
      if (!savedCode) {
        setError(failedMessage);
        return;
      }
      onSaved(savedCode);
      onClose();
    } catch (saveError) {
      // The dialog stays open so a name the API already holds can be corrected here.
      setError(resolveSaveErrorMessage(saveError, failedMessage));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const title = isEdit ? "แก้ไขหมวดสินค้า" : "เพิ่มหมวดสินค้า";

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ width: DIALOG_WIDTH, maxWidth: "100%" }}>
        <FieldLabel required>ชื่อหมวดสินค้า</FieldLabel>
        <TextField
          size="small"
          fullWidth
          autoFocus
          value={name}
          onChange={handleChangeName}
          placeholder="กรอกชื่อหมวดสินค้า"
          error={Boolean(error)}
          helperText={error}
          slotProps={{ htmlInput: { maxLength: MAX_NAME_LENGTH } }}
        />
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={handleClose}>
          ยกเลิก
        </Button>
        <Button loading={saving} variant="contained" onClick={handleSubmit}>
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
